import 'regenerator-runtime/runtime';
import { call, cancel, select, takeEvery } from 'redux-saga/effects';

import {
  getAuslandsRisikoZuschlag,
  getGeschaeftsart,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  VorschlagsdatenRequestPayload,
} from '@ps20-frontend/shop-integration-redux';
import {
  AdresseAntrag,
  EinheitenArt,
  FamilienStand,
  Feature,
  RsVermietungDatenAntrag,
  SpartenType,
  VermieteteEinheit,
  VermietetesGrundstueck,
  VermietetesObjektAntrag,
  VorschadenAntrag,
  VorschadenArt,
} from '@ps20/bff-models';
import {
  App,
  convertToBffLaufzeit,
  DIPLOMATEN_CODE,
  getFachschluessel,
  mapToBffVorvertragsNummer,
  VermietetesBebautesObjekt,
  VermietetesModalObjekt,
  VermietetesObjektEnum,
  VermietetesUnbebautesObjekt,
} from '@ps20-frontend/frontend-models';
import dayjs from 'dayjs';
import { convertBasisAdresseToBffAdresse } from '@ps20-frontend/vorschlagsdaten-lib';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { DATEFORMAT_SHORT_ISO } from '@ps20-frontend/utils';
import { mapGesamtbeitragIfJaehrlichAndBeitragIsWrong } from '@ps20-frontend/preis-lib';
import { selectFeatureToggle } from '@ps20-frontend/selector';
import { IAppState } from '../appState';
import { selectSelectedProduktlinie } from '../../app/shared/selectors/produkt.selector';

function mapFamilienStand(versicherungsnehmer: IAppState['versicherungsnehmer']) {
  if (versicherungsnehmer.familienstand) {
    return versicherungsnehmer.familienstand;
  }
  return versicherungsnehmer.juristisch ? FamilienStand.FAMILIE : null;
}

export function* watchVorschlagdatenChanged() {
  yield takeEvery(
    GlobalActions.VORSCHLAGDATEN_REQUESTED,
    safeSaga(vorschlagdatenSaga, App.RS_VERMIETUNG),
  );
}

export function* vorschlagdatenSaga(action: GlobalAction<VorschlagsdatenRequestPayload>) {
  const { angebotsIds } = action.payload;

  if (!angebotsIds.some(angebot => angebot.appId === App.RS_VERMIETUNG)) {
    yield cancel();
    return;
  }

  const state: IAppState = yield select();
  const globalPut = GlobalPut.getInstance();

  const ausgewaehlteProduktlinie = selectSelectedProduktlinie(state);
  const vorteilsRabattActivated = selectFeatureToggle(state, Feature.VORTEILSRABATT);

  const vorschlagdaten: RsVermietungDatenAntrag = {
    spartenType: SpartenType.RECHTSSCHUTZ_VERMIETUNG,
    jahresbeitrag: state.jahresbeitrag,
    angebotsdatum: vorteilsRabattActivated ? state.vertragsoptionen.angebotsdatum : undefined,
    savedJahresbeitragMitRabatt: state.savedJahresbeitragMitRabatt,
    gesamtpreisNachZahlweise: mapGesamtbeitragIfJaehrlichAndBeitragIsWrong(
      state.vertragsoptionen.zahlungsweise,
      state.gesamtbeitragMitRabatt,
      state.jahresbeitrag,
      App.RS_VERMIETUNG,
    ),
    savedGesamtbeitragMitRabatt: state.savedGesamtbeitragMitRabatt,
    familienStand: mapFamilienStand(state.versicherungsnehmer),
    vnAdresse: convertBasisAdresseToBffAdresse(state.risikoort),
    vermieteteObjekte: convertVermieteteObjekte(state),
    vermieteteGrundstuecke: convertVermieteteGrundstuecke(state),
    antragsId: state.antragsnummer,
    hspLfnr: state.produkt.hspLfnr,
    sicherungsglaeubiger: null,
    auslandsRisikoZuschlag: getAuslandsRisikoZuschlag(
      state.vertragsoptionen?.selectedKooperation === DIPLOMATEN_CODE,
      ausgewaehlteProduktlinie.nachlass,
    ),
    produkt: {
      beginn: null,
      ablauf: null,
      selbstbeteiligung: state.vertragsoptionen.selbstbeteiligung,
      produktline: ausgewaehlteProduktlinie.produktName,
      kurztext: ausgewaehlteProduktlinie.kurztext,
      langtext: ausgewaehlteProduktlinie.langtext,
      fachschluessel: getFachschluessel(state.produkt),
    },
    vorschaeden: convertToVorschaeden(state),
    rbdMax: ausgewaehlteProduktlinie.rbdMax,
    rbdAktuell: state.taasInfo.rbdNachlass,
    treuebonus: state.taasInfo.tbNachlass,
    savedRbdNachlass: state.taasInfo.savedRbdNachlass,
    savedTbNachlass: state.taasInfo.savedTbNachlass,
    vorteilsrabatt: state.taasInfo.vorteilsrabatt,
    preisverzeichnis: ausgewaehlteProduktlinie.preisverzeichnis,
    vorversicherung: [],
    antragSpeichernDatum: dayjs().format(DATEFORMAT_SHORT_ISO),
    laufzeit: convertToBffLaufzeit(state.vertragsoptionen.vertragslaufzeit),
    rabattstufe: state.rabattstufe,
    geschaeftsart: getGeschaeftsart(state.vertragsoptionen),
    vorvertragsnummern: mapToBffVorvertragsNummer(state.vertragsoptionen.vorvertragsNummern),
    vorvertragGekuendigt: state.vertragsoptionen.vorvertragGekuendigt,
    zielmarktAbweichung: false,
    kooperation: state.vertragsoptionen.selectedKooperation,
  };

  yield call(globalPut.put, new GlobalActions().vorschlagsdatenGenerated(vorschlagdaten));
}

const DEFAULT_BETRACHTUNGSZEITRAUM_RS = 5;

export function convertToVorschaeden(state: IAppState): VorschadenAntrag[] {
  return [
    {
      vorschadenArt: VorschadenArt.RS,
      anzahl: state.rechtsstreitigkeiten.anzahl,
      betrachtungszeitraum: DEFAULT_BETRACHTUNGSZEITRAUM_RS,
    },
  ];
}

export function convertVermieteteObjekte(state: IAppState): Array<VermietetesObjektAntrag> {
  const result: Array<VermietetesObjektAntrag> = new Array<VermietetesObjektAntrag>();
  const vermieteteObjekte: Array<VermietetesModalObjekt> = state.vermieteteCardObjekte;

  vermieteteObjekte
    .filter(
      vermietetesObjektFrontend =>
        vermietetesObjektFrontend.grundstueckBebaut === VermietetesObjektEnum.BEBAUT,
    )
    .map(vermietetesObjektFrontend => vermietetesObjektFrontend as VermietetesBebautesObjekt)
    .forEach(vermietetesObjektFrontend => {
      const adresseAntrag: AdresseAntrag = {
        strasse: vermietetesObjektFrontend.strasse,
        hausnummer: vermietetesObjektFrontend.hausnummer,
        ort: vermietetesObjektFrontend.ort,
        plz: vermietetesObjektFrontend.plz,
      };
      const vermieteteEinheiten: Array<VermieteteEinheit> = new Array<VermieteteEinheit>();
      if (vermietetesObjektFrontend.bruttojahresmieteWohneinheiten) {
        vermieteteEinheiten.push({
          jahresBruttoMiete: vermietetesObjektFrontend.bruttojahresmieteWohneinheiten,
          einheitenArt: EinheitenArt.WOHNEINHEIT,
          anzahlVermieteteEinheiten: vermietetesObjektFrontend.wohneinheiten,
          deltafaktor: vermietetesObjektFrontend.deltafaktorWohnung,
          plzFaktor: vermietetesObjektFrontend.plzFaktorWohnung,
        });
      }
      if (vermietetesObjektFrontend.bruttojahresmieteGewerbeeinheiten) {
        vermieteteEinheiten.push({
          jahresBruttoMiete: vermietetesObjektFrontend.bruttojahresmieteGewerbeeinheiten,
          einheitenArt: EinheitenArt.GEWERBEEINHEIT,
          anzahlVermieteteEinheiten: vermietetesObjektFrontend.gewerbeeinheiten,
          deltafaktor: vermietetesObjektFrontend.deltafaktorGewerbe,
          plzFaktor: vermietetesObjektFrontend.plzFaktorGewerbe,
        });
      }
      if (vermietetesObjektFrontend.bruttojahresmieteStellplaetze) {
        vermieteteEinheiten.push({
          jahresBruttoMiete: vermietetesObjektFrontend.bruttojahresmieteStellplaetze,
          einheitenArt: EinheitenArt.GARAGE,
          anzahlVermieteteEinheiten: vermietetesObjektFrontend.stellplaetze,
          deltafaktor: vermietetesObjektFrontend.deltafaktorGarage,
          plzFaktor: vermietetesObjektFrontend.plzFaktorGarage,
        });
      }

      result.push({
        adresseAntrag: adresseAntrag,
        vermieteteEinheiten: vermieteteEinheiten,
      } as VermietetesObjektAntrag);
    });
  return result;
}

export function convertVermieteteGrundstuecke(state: IAppState): Array<VermietetesGrundstueck> {
  const vermieteteObjekte: Array<VermietetesModalObjekt> = state.vermieteteCardObjekte;
  if (!vermieteteObjekte) {
    return;
  }
  return vermieteteObjekte
    .filter(
      vermietetesObjektFrontend =>
        vermietetesObjektFrontend.grundstueckBebaut === VermietetesObjektEnum.UNBEBAUT,
    )
    .map(vermObjekt => vermObjekt as VermietetesUnbebautesObjekt)
    .map(vermietetesGruendstuck => {
      const adresseAntrag: AdresseAntrag = {
        strasse: vermietetesGruendstuck.strasse,
        hausnummer: vermietetesGruendstuck.hausnummer,
        ort: vermietetesGruendstuck.ort,
        plz: vermietetesGruendstuck.plz,
      };
      return {
        adresseAntrag: adresseAntrag,
        unbebauteFlaeche: vermietetesGruendstuck.grundstueckFlaeche,
        flurstueckNummer: vermietetesGruendstuck.flurstueckNummer,
        plzFaktor: vermietetesGruendstuck.plzFaktorUnbebaut,
        deltafaktor: vermietetesGruendstuck.deltafaktorUnbebaut,
      };
    });
}
